import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["resultCount", "nameField", "clearButton"]
  
  connect() {
    this.timeout = null
    this.updateResultCount()
    this.setupNameFieldListener()
    this.updateClearButtonVisibility()
  }
  
  submit(event) {
    // Submit the form immediately for dropdown changes
    this.element.requestSubmit()
    // Update clear button visibility after form submission
    setTimeout(() => this.updateClearButtonVisibility(), 100)
  }
  
  debouncedSubmit(event) {
    // Check if LinkedIn URL is detected in name field
    this.checkForLinkedInUrl(event.target)
    
    // Debounce text input changes
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => {
      this.element.requestSubmit()
      // Update clear button visibility after form submission
      setTimeout(() => this.updateClearButtonVisibility(), 100)
    }, 500)
  }
  
  setupNameFieldListener() {
    // Find the name field and add special handling for LinkedIn URLs
    const nameField = this.element.querySelector('input[name="name"]')
    if (nameField) {
      nameField.setAttribute('data-lead-filters-target', 'nameField')
      this.checkForLinkedInUrl(nameField)
    }
  }
  
  checkForLinkedInUrl(field) {
    if (!field || field.name !== 'name') return
    
    const value = field.value.trim()
    const isLinkedInUrl = /(?:https?:\/\/)?(?:www\.)?linkedin\.com\/in\//.test(value)
    
    if (isLinkedInUrl) {
      // Add visual indication that LinkedIn URL is detected
      field.classList.add('ring-2', 'ring-blue-500', 'border-blue-500')
      field.setAttribute('title', 'LinkedIn profile URL detected - searching by LinkedIn profile')
      
      // Optionally update placeholder to show it's recognized
      if (!field.dataset.originalPlaceholder) {
        field.dataset.originalPlaceholder = field.placeholder
      }
      field.placeholder = "LinkedIn URL detected..."
    } else {
      // Remove LinkedIn indication
      field.classList.remove('ring-2', 'ring-blue-500', 'border-blue-500')
      field.setAttribute('title', 'Search by name or paste a LinkedIn profile URL')
      
      // Restore original placeholder
      if (field.dataset.originalPlaceholder) {
        field.placeholder = field.dataset.originalPlaceholder
      }
    }
  }
  
  updateResultCount() {
    // Update the result count if the target exists
    if (this.hasResultCountTarget) {
      const params = new URLSearchParams(window.location.search)
      const filterCount = Array.from(params.entries()).filter(([key, value]) => {
        return value && !['view', 'sort_by', 'page'].includes(key)
      }).length
      
      if (filterCount > 0) {
        this.resultCountTarget.textContent = `${filterCount} filter${filterCount === 1 ? '' : 's'} active`
      } else {
        this.resultCountTarget.textContent = "No filters active"
      }
    }
  }
  
  updateClearButtonVisibility() {
    if (this.hasClearButtonTarget) {
      const hasActiveFilters = this.hasActiveFilters()
      if (hasActiveFilters) {
        this.clearButtonTarget.classList.remove('hidden')
        // Update the count in the button text
        const countElement = this.clearButtonTarget.querySelector('[data-filter-count]')
        if (countElement) {
          countElement.textContent = this.getActiveFilterCount()
        }
      } else {
        this.clearButtonTarget.classList.add('hidden')
      }
    }
  }
  
  hasActiveFilters() {
    // Check form inputs for values (excluding navigation params)
    const formData = new FormData(this.element)
    const excludedParams = ['view', 'sort_by', 'page', 'per_page', 'q[s]']
    
    for (let [key, value] of formData.entries()) {
      if (value && value.trim() !== '' && !excludedParams.includes(key)) {
        return true
      }
    }
    return false
  }
  
  getActiveFilterCount() {
    const formData = new FormData(this.element)
    const excludedParams = ['view', 'sort_by', 'page', 'per_page', 'q[s]']
    let count = 0
    
    for (let [key, value] of formData.entries()) {
      if (value && value.trim() !== '' && !excludedParams.includes(key)) {
        count++
      }
    }
    return count
  }
  
  clearFilters(event) {
    event.preventDefault()
    
    // Clear all form inputs except preserved ones
    const form = this.element
    const preservedParams = ['view']
    
    // Clear text inputs
    form.querySelectorAll('input[type="text"]').forEach(input => {
      if (!preservedParams.includes(input.name)) {
        input.value = ''
      }
    })
    
    // Reset select dropdowns to first option (usually "All" or empty)
    form.querySelectorAll('select').forEach(select => {
      if (!preservedParams.includes(select.name)) {
        select.selectedIndex = 0
      }
    })
    
    // Update clear button visibility immediately (should hide it)
    this.updateClearButtonVisibility()
    
    // Submit the cleared form
    form.requestSubmit()
  }
}